// src/components/AdminServiciosModal.jsx
import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaTimes, FaBolt, FaChartLine, FaCubes, FaLayerGroup, FaMobileAlt, FaRobot } from 'react-icons/fa';

const iconMap = {
  bolt: FaBolt,
  chart: FaChartLine,
  cubes: FaCubes,
  layers: FaLayerGroup,
  mobile: FaMobileAlt,
  robot: FaRobot,
};

import AdminServicioForm from './AdminServicioForm.js';
import ServiceActionsDropdown from './ServiceActionsDropdown.js';
import ConfirmModal from './ConfirmModal.js';
import EditServiceModal from './EditServiceModal.js'; // Modal de edición
import Loader from './shared/Loader.js';

import {
  listServicios,
  createServicio,
  deleteServicio
} from '../services/servicioService.js';

import { AuthContext } from '../context/AuthContext.js';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Animaciones de Framer Motion
const getModalVariants = (direction = 'forward') => {
  return direction === 'forward'
    ? {
      hidden: { opacity: 0, x: '100vw' },
      visible: { opacity: 1, x: '0' },
      exit: { opacity: 0, x: '-100vw' },
    }
    : {
      hidden: { opacity: 0, x: '-100vw' },
      visible: { opacity: 1, x: '0' },
      exit: { opacity: 0, x: '100vw' },
    };
};

const AdminServiciosModal = ({ isOpen, onClose, direction = 'forward' }) => {
  const { t } = useTranslation();
  const { accessToken } = useContext(AuthContext);
  const variants = getModalVariants(direction);

  // Estados principales
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Búsqueda por nombre
  const [searchTerm, setSearchTerm] = useState('');

  // Crear servicio
  const [newServicio, setNewServicio] = useState({
    nombre: '',
    descripcion: '',
    precio: '',
    icono: '',
  });

  // Confirmación de eliminación
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [servicioToDelete, setServicioToDelete] = useState(null);

  // Edición: modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [servicioToEdit, setServicioToEdit] = useState(null);

  // Efecto: cargar lista de servicios al abrir el modal
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    listServicios()
      .then((data) => {
        setServicios(data.servicios || data);
        setLoading(false);
      })
      .catch((err) => {
        const errMsg = err.message || t('adminServiciosModal.errorLoad');
        setError(errMsg);
        setLoading(false);
        toast.error(errMsg);
      });
  }, [isOpen, t]);

  // Filtrar por searchTerm (incluye nombres traducidos)
  const filteredServicios = (servicios || []).filter((servicio) => {
    const displayName = servicio.nombre?.includes('.') ? t(servicio.nombre) : (servicio.nombre || '');
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Crear servicio
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newServicio.nombre.trim()) {
      toast.error(t('adminServiciosModal.toast.createMissingFields'));
      return;
    }
    try {
      const response = await createServicio(newServicio, accessToken);
      setServicios((prev) => [...prev, response.servicio]);
      setNewServicio({ nombre: '', descripcion: '', precio: '', icono: '' });
      toast.success(t('adminServiciosModal.toast.createSuccess'));
    } catch (err) {
      console.error('Error al crear servicio:', err);
      toast.error(t('adminServiciosModal.toast.createError'));
    }
  };

  // Eliminar servicio
  const handleDelete = (id) => {
    setServicioToDelete(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!servicioToDelete) return;
    try {
      await deleteServicio(servicioToDelete, accessToken);
      setServicios((prev) =>
        prev.filter((s) => s.idServicio !== servicioToDelete)
      );
      toast.success(t('adminServiciosModal.toast.deleteSuccess'));
    } catch (err) {
      console.error('Error al eliminar servicio:', err);
      toast.error(t('adminServiciosModal.toast.deleteError'));
    } finally {
      setConfirmOpen(false);
      setServicioToDelete(null);
    }
  };

  // Editar servicio
  const handleEditService = (servicio) => {
    setServicioToEdit(servicio);
    setEditModalOpen(true);
  };

  // Manejo de la ventana (overlay)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const imageSrc = windowWidth < 800 ? '/images/PatronDos.png' : '/images/patronUno.png';

  const handleOverlayClick = (e) => {
    const selectedText = window.getSelection().toString();
    if (selectedText.length > 0) return;
    onClose();
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            className="modal-overlay admin-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleOverlayClick}
          >
            <motion.div
              className="modal-content admin-submodal servicios"
              variants={variants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="admin-submodal-header">
                <button className="back-button" onClick={onClose}>
                  <FaArrowLeft />
                </button>
                <button className="close-icon" onClick={onClose}>
                  <FaTimes />
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="leftModal">
                  <img src={imageSrc} alt={t('adminServiciosModal.altImage')} />
                </div>

                <div className="rightModal">
                  <h2>{t('adminServiciosModal.title')}</h2>

                  <div className="search-container">
                    <input
                      type="text"
                      placeholder={t('adminServiciosModal.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {loading && <Loader message={t('adminServiciosModal.loading')} />}
                  {error && <p className="error">{error}</p>}

                  <div className="admin-section-title">
                    {t('adminServiciosModal.currentServices', 'Actuales')}
                  </div>

                  <div className="projects-list">
                    {filteredServicios.map((servicio) => {
                      const ServiceIcon = iconMap[servicio.icono] || FaBolt;
                      return (
                        <div key={servicio.idServicio} className="project-item">
                          <div className="project-info">
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <ServiceIcon style={{ opacity: 0.7, flexShrink: 0 }} />
                              {servicio.nombre?.includes('.') ? t(servicio.nombre) : servicio.nombre}
                            </h3>
                            <p>{servicio.descripcion?.includes('.') ? t(servicio.descripcion) : servicio.descripcion}</p>
                            {servicio.icono && (
                              <p style={{ fontSize: '0.78rem', opacity: 0.55 }}>
                                ícono: <code>{servicio.icono}</code>
                              </p>
                            )}
                          </div>
                          <ServiceActionsDropdown
                            onEdit={() => handleEditService(servicio)}
                            onDelete={() => handleDelete(servicio.idServicio)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="admin-section-title">
                    {t('adminServiciosModal.addNew', 'Nuevo Servicio')}
                  </div>

                  <AdminServicioForm
                    newServicio={newServicio}
                    setNewServicio={setNewServicio}
                    handleCreate={handleCreate}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={confirmOpen}
        message={t('adminServiciosModal.confirmMessage')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmOpen(false);
          setServicioToDelete(null);
        }}
      />

      <EditServiceModal
        isOpen={editModalOpen}
        service={servicioToEdit}
        onClose={() => {
          setEditModalOpen(false);
          setServicioToEdit(null);
        }}
        onSave={(updatedServicio) => {
          setServicios((prev) =>
            prev.map((s) => (s.idServicio === updatedServicio.idServicio ? updatedServicio : s))
          );
        }}
        accessToken={accessToken}
      />
    </>
  );
};

export default AdminServiciosModal;